<script>
import baseMixin from '@/mixins/baseMixin'
import { mapGetters, mapActions } from 'vuex'
import EmptyStateBlock from '@/components/general/empty-state-block.vue'
import SideDrawerDialog from '@/components/general/side-drawer-dialog.vue'
import BookingDetailsContent from '@/components/booking/details/booking-details-content.vue'
import BookingPendingApprovalsDetailsDialogHeader from '@/components/booking/pendingApprovals/booking-pending-approvals-details-dialog-header.vue'

export default {
  name: 'ApprovalContainer',
  components: {
    SideDrawerDialog,
    BookingPendingApprovalsDetailsDialogHeader,
    BookingDetailsContent,
    EmptyStateBlock,
  },
  mixins: [baseMixin],
  props: {
    value: { type: Boolean, default: false },
    activeBookingId: { type: Number, default: null },
  },
  computed: {
    ...mapGetters({
      isLoadingFlyout: 'bookingApprovals/isLoadingFlyout',
      activeBooking: 'bookingApprovals/activeBooking',
      flyoutHasError: 'bookingApprovals/flyoutHasError',
      showFlyout: 'bookingApprovals/showFlyout',
      showDetailsContent: 'bookingApprovals/showDetailsContent',
    }),
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.handleApproveRejectBooking()
      }
    },
  },
  methods: {
    ...mapActions({
      resetFlyout: 'bookingApprovals/resetFlyout',
      openFlyout: 'bookingApprovals/openFlyout',
      setFlyout: 'bookingApprovals/setFlyout',
      loadApprovalDetails: 'bookingApprovals/loadApprovalDetails',
      submitPendingApproval: 'bookingApprovals/submitPendingApproval',
    }),
    async closeFlyout() {
      this.$emit('update:activeBookingId', null)
      this.$emit('input', false)
      await this.resetFlyout()
    },
    async loadBookingDetails() {
      await this.setFlyout({
        key: 'error',
        value: false,
      })
      await this.openFlyout()
      const { isSuccess, data } = await this.loadApprovalDetails(
        this.activeBookingId
      )

      if (!isSuccess) {
        return await this.setFlyout({
          key: 'error',
          value: true,
        })
      }

      await this.setFlyout({
        key: 'booking',
        value: data,
      })
    },
    async handleApproveRejectBooking() {
      await this.loadBookingDetails()
    },
    async submitPendingApprovalResponse(payload) {
      const { isSuccess } = await this.submitPendingApproval(payload)

      if (!isSuccess) return

      this.closeFlyout()
    },
    async handleEvent(payload) {
      switch (payload.event) {
        case 'submitPendingApproval':
          return await this.submitPendingApprovalResponse(payload.value)
        default:
          throw new Error(`Unhandled event: ${payload.event}`)
      }
    },
  },
}
</script>

<template>
  <div>
    <SideDrawerDialog
      :show="showFlyout"
      :xl="5"
      :lg="6"
      :md="10"
      @closeDialog="closeFlyout"
    >
      <template v-slot:header>
        <BookingPendingApprovalsDetailsDialogHeader @close="closeFlyout" />
      </template>

      <div
        v-if="isLoadingFlyout"
        class="w-full h-100 d-flex justify-center align-center"
      >
        <v-progress-circular size="60" color="primary" indeterminate />
      </div>

      <EmptyStateBlock
        v-else-if="flyoutHasError"
        :title="$t('booking.details.loading.failure.title')"
        :description="$t('booking.details.loading.failure.description')"
        border
      >
        <template v-slot:button>
          <div class="mt-4">
            <v-btn color="primary" depressed large @click="loadBookingDetails">
              <v-icon left> mdi-refresh </v-icon>
              {{ $t('general.retry') }}
            </v-btn>
          </div>
        </template>
      </EmptyStateBlock>

      <BookingDetailsContent
        v-else-if="showDetailsContent"
        :booking="activeBooking"
        @actionEvent="handleEvent"
      />
      <template v-slot:actions> </template>
    </SideDrawerDialog>
  </div>
</template>
